import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../app/store';
import { Weave } from '../../models/weave';
import { loadBuildFulfilled } from './weaveLoadSlice';
import { decodeBuildFulfilled } from './weaveShareSlice';
import { weaveTrainerApi } from './weaveTrainerApi';
import { weaveTrainerData } from './weaveTrainerData';

const data = {
  weaves: weaveTrainerData.getWeaves()
}

let getTrainerResultsTimeoutId: NodeJS.Timeout | undefined;

const initialState: WeaveTrainerState = {
  intl: 18,
  rank: 0,
  optimized: true,
  weaves: data.weaves,
  commands: '',
  pracs: 0,
  pracs2: 0,
  remainderPracs: 0,
  level: 1,
  earth: 0,
  air: 0,
  fire: 0,
  water: 0,
  spirit: 0
};

export interface WeaveTrainerState {
  intl: number;
  rank: number;
  optimized: boolean;
  weaves: Weave[];
  commands: string;
  pracs: number;
  pracs2: number;
  remainderPracs: number;
  level: number;
  earth: number;
  air: number;
  fire: number;
  water: number;
  spirit: number;
}

export const slice = createSlice({
  name: 'weaveTrainer',
  initialState,
  reducers: {
    reset: (state) =>{
      clearTimeout(getTrainerResultsTimeoutId);

      state.level = 1;
      state.pracs = 0;
      state.pracs2 = 0;
      state.remainderPracs = 0;
      state.commands = '';
      state.earth = 0;
      state.air = 0;
      state.fire = 0;
      state.water = 0;
      state.spirit = 0;

      for (let weave of state.weaves) {
        weave.percentStart = 0;
        weave.percentTarget = 0;
        weave.percentActual = 0;
        weave.percentHistory = [];
        weave.pracsActual = 0;
        weave.pracsCount = 0;
        weave.percentActual2 = 0;
        weave.percentHistory2 = [];
        weave.pracsActual2 = 0;
        weave.pracsCount2 = 0;
        weave.busy = false;
      }
    },

    setIntl: (state, action: PayloadAction<number>) => {
      state.intl = action.payload;
    },
    
    setRank: (state, action: PayloadAction<number>) => {
      state.rank = action.payload;
    },

    setOptimized: (state, action: PayloadAction<boolean>) => {
      state.optimized = action.payload;
    },

    setWeaveTargetPercent: (state, action: PayloadAction<{weaveId: string, percent: number}>) => {
      const { weaveId, percent } = action.payload;
      const weave = state.weaves.find(x => x.id === weaveId);
      if (weave) {
        weave.percentTarget = percent;
        weave.percentActual = 0;
        weave.percentHistory = [];
        weave.pracsActual = 0;
        weave.pracsCount = 0;
        weave.percentActual2 = 0;
        weave.percentHistory2 = [];
        weave.pracsActual2 = 0;
        weave.pracsCount2 = 0;
        weave.busy = true;
      }
    },

    setWeaveStartPercent: (state, action: PayloadAction<{weaveId: string, percent: number}>) => {
      const { weaveId, percent } = action.payload;
      const weave = state.weaves.find(x => x.id === weaveId);
      if (weave) {
        weave.percentStart = percent;
        weave.percentActual = 0;
        weave.percentHistory = [];
        weave.pracsActual = 0;
        weave.pracsCount = 0;
        weave.percentActual2 = 0;
        weave.percentHistory2 = [];
        weave.pracsActual2 = 0;
        weave.pracsCount2 = 0;
        weave.busy = true;
      }
    },
    
    getTrainerResultsPending: (state) => {
      //TODO
    },

    getTrainerResultsRejected: (state) => {
      for (let weave of state.weaves) {
        weave.busy = false;
      }
    },
    
    getTrainerResultsFulfilled: (state, action: PayloadAction<any>) => {
      console.log('api result', action.payload);
      const result = action.payload;
      state.commands = result.commands;
      state.pracs = result.totalPracs;
      state.pracs2 = result.totalPracs2;
      state.remainderPracs = result.remainderPracs;
      state.level = result.level;
      state.earth = result.earth;
      state.air = result.air;
      state.fire = result.fire;
      state.water = result.water;
      state.spirit = result.spirit;
      //state.residualOrderIds = result.residualOrderIds;
      //state.residualOrderType = result.residualOrderType;

      for (let weave of state.weaves) {
        weave.percentActual = 0;
        weave.percentHistory = [];
        weave.pracsActual = 0;
        weave.pracsCount = 0;
        weave.percentActual2 = 0;
        weave.percentHistory2 = [];
        weave.pracsActual2 = 0;
        weave.pracsCount2 = 0;
        weave.busy = false;
      }

      for (let weaveResult of result.weaves) {
        const weave = state.weaves.find(x => x.id === weaveResult.id);
        if (weave) {
          weave.percentActual = weaveResult.percentActual;
          weave.pracsActual = weaveResult.pracsActual;
          weave.percentHistory = weaveResult.percentHistory;
          weave.pracsCount = weaveResult.pracsCount;
          weave.percentActual2 = weaveResult.percentActual2;
          weave.pracsActual2 = weaveResult.pracsActual2;
          weave.percentHistory2 = weaveResult.percentHistory2;
          weave.pracsCount2 = weaveResult.pracsCount2;
        }
      }
    }
  },
  
  extraReducers: builder => {
    builder.addCase(loadBuildFulfilled, (state, action) => {
      slice.caseReducers.reset(state);

      state.intl = action.payload.intl;
      state.rank = action.payload.rank;
      
      for (let savedWeave of action.payload.weaves) {
        const weave = state.weaves.find(x => x.id === savedWeave.id);
        if (weave) {
          weave.percentStart = 0;
          weave.percentTarget = savedWeave.percentTarget;
        }
      }
    });

    builder.addCase(decodeBuildFulfilled, (state, action) => {
      slice.caseReducers.reset(state);

      state.intl = action.payload.intl ?? 0;
      state.rank = action.payload.rank ?? 0;
      
      for (let savedWeave of action.payload.weaves) {
        const weave = state.weaves.find(x => x.idNum === savedWeave.idNum);
        if (weave) {
          weave.percentStart = 0;
          weave.percentTarget = savedWeave.percentTarget ?? 0;
        }
      }
    });
  }
});

export const getTrainerResults = (): AppThunk => (dispatch, getState) => {
  clearTimeout(getTrainerResultsTimeoutId);
  dispatch(slice.actions.getTrainerResultsPending());
  getTrainerResultsTimeoutId = setTimeout(async () => {
    try {
      const { optimized, intl, rank, weaves } = getState().weaveTrainer;
      const targets = weaves.map(x => ({ nameId: x.id, target: x.percentTarget, start: x.percentStart }));
      const data = await weaveTrainerApi.getTrainerResults(optimized, intl, rank, targets);
      dispatch(slice.actions.getTrainerResultsFulfilled(data));
    } catch (ex) {
      dispatch(slice.actions.getTrainerResultsRejected());
    }
  }, 300);
};

export default slice.reducer;
export const { reset, setIntl, setRank, setOptimized, setWeaveTargetPercent, setWeaveStartPercent } = slice.actions;